import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { useAdminEmployees } from '@/common/hooks/useAdminEmployees'
import { fetchApi } from '@/common/lib/fetchApi'
import { Text } from '@/common/components/text'
import { Stack } from '@/common/components/stack'
import { colors } from '@/core/styles/tokens'

type TabStatus = 'matched' | 'unmatched' | 'template_skipped' | 'error'

interface PeriodSummary {
  start_date: string
  end_date: string
  entry_count: number
  total_hours: number
  total_standard: number
  total_flsa_overtime: number
  total_straight_overtime: number
}

interface PreviewTab {
  sheet_name: string
  status: TabStatus
  employee_id: number | null
  employee_label: string | null
  periods: PeriodSummary[]
  entry_count: number
  total_hours: number
  error: string | null
}

interface BulkPreviewResponse {
  tabs: PreviewTab[]
  matched_tab_count: number
  total_hours: number
  total_entry_count: number
}

interface ImportedTimesheetInfo {
  timesheet_id: number
  start_date: string
  end_date: string
  status: string
  total_hours: number
  entry_count: number
  entries_added: number
  entries_updated: number
  was_created: boolean
}

interface BulkImportTab {
  sheet_name: string
  employee_id: number
  timesheets: ImportedTimesheetInfo[]
}

interface BulkImportResponse {
  tabs: BulkImportTab[]
  imported_tab_count: number
  total_hours: number
  total_entry_count: number
}

function statusColor(status: TabStatus): string {
  if (status === 'matched') return colors.success
  if (status === 'unmatched') return colors.caution
  if (status === 'template_skipped') return colors.gray70
  return colors.error
}

function statusLabel(status: TabStatus): string {
  if (status === 'template_skipped') return 'template (skipped)'
  return status
}

function ImportBulkPage() {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<BulkPreviewResponse | null>(null)
  const [mapping, setMapping] = useState<Record<string, number | null>>({})
  const [importResult, setImportResult] = useState<BulkImportResponse | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const { data: employees } = useAdminEmployees()

  const previewMutation = useMutation({
    mutationFn: async (selectedFile: File) => {
      const formData = new FormData()
      formData.append('file', selectedFile)
      return fetchApi<BulkPreviewResponse>('/api/org/import-timesheet/bulk-preview', {
        method: 'POST',
        body: formData,
      })
    },
    onSuccess: (data) => {
      setPreview(data)
      setImportResult(null)
      // Seed mapping from auto-resolved matches.
      const seeded: Record<string, number | null> = {}
      for (const tab of data.tabs) {
        if (tab.status === 'matched' || tab.status === 'unmatched') {
          seeded[tab.sheet_name] = tab.employee_id ?? null
        }
      }
      setMapping(seeded)
    },
  })

  const importMutation = useMutation({
    mutationFn: async (args: { file: File; mapping: Record<string, number | null> }) => {
      const formData = new FormData()
      formData.append('file', args.file)
      // Strip null entries so the server only sees tabs the admin actually mapped.
      const cleanMapping = Object.fromEntries(
        Object.entries(args.mapping).filter(([, v]) => v !== null && v !== undefined)
      )
      formData.append('mapping', JSON.stringify(cleanMapping))
      return fetchApi<BulkImportResponse>('/api/org/import-timesheet/bulk', {
        method: 'POST',
        body: formData,
      })
    },
    onSuccess: (data) => {
      setImportResult(data)
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPreview(null)
    setImportResult(null)
    setMapping({})
    setFileError(null)
    const selected = e.target.files?.[0] ?? null
    if (selected && !selected.name.toLowerCase().endsWith('.xlsx')) {
      setFileError('Only .xlsx files are supported')
      setFile(null)
      return
    }
    setFile(selected)
  }

  const handlePreview = () => {
    if (!file) return
    previewMutation.mutate(file)
  }

  const handleImport = () => {
    if (!file) return
    importMutation.mutate({ file, mapping })
  }

  const handleMappingChange = (sheetName: string, value: string) => {
    setImportResult(null)
    setMapping((prev) => ({
      ...prev,
      [sheetName]: value === '' ? null : Number(value),
    }))
  }

  const hasMappedTabs = Object.values(mapping).some((v) => v !== null && v !== undefined)
  const hasMappedErrorTab =
    preview?.tabs.some(
      (t) => t.status === 'error' && mapping[t.sheet_name] !== null && mapping[t.sheet_name] !== undefined
    ) ?? false
  const canImport = !!preview && hasMappedTabs && !hasMappedErrorTab && !importMutation.isPending

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400 }}>
      <Stack gap="16">
        <Text size="xl" weight="bold">
          Bulk Import Timesheets from Excel
        </Text>
        <Text size="sm" style={{ color: colors.gray70 }}>
          Upload an .xlsx workbook with one tab per employee. The `template` tab is skipped. Each tab is matched to an employee by name (or email);
          unmatched tabs can be mapped manually below.
        </Text>

        <div>
          <input type="file" accept=".xlsx" onChange={handleFileChange} />
          {file && (
            <span style={{ marginLeft: 12, fontSize: 13, color: colors.martinique }}>
              {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </span>
          )}
          {fileError && (
            <div style={{ marginTop: 8, color: colors.error, fontSize: 13 }}>{fileError}</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handlePreview}
            disabled={!file || previewMutation.isPending}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: 'none',
              background: file ? colors.sea : colors.gray50,
              color: colors.white,
              fontSize: 14,
              cursor: file ? 'pointer' : 'default',
            }}
          >
            {previewMutation.isPending ? 'Parsing...' : 'Preview'}
          </button>

          {preview && (
            <button
              onClick={handleImport}
              disabled={!canImport}
              style={{
                padding: '8px 20px',
                borderRadius: 6,
                border: 'none',
                background: canImport ? colors.success : colors.gray50,
                color: colors.white,
                fontSize: 14,
                cursor: canImport ? 'pointer' : 'default',
              }}
            >
              {importMutation.isPending ? 'Importing...' : 'Import'}
            </button>
          )}
        </div>

        {previewMutation.isError && (
          <div style={errorBox}>{(previewMutation.error as Error).message}</div>
        )}

        {importMutation.isError && (
          <div style={errorBox}>{(importMutation.error as Error).message}</div>
        )}

        {preview && (
          <div>
            <Text size="lg" weight="bold">
              Preview ({preview.matched_tab_count} matched, {preview.total_entry_count} entries, {preview.total_hours.toFixed(2)} hours)
            </Text>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.gray20}` }}>
                  <th style={th}>Tab</th>
                  <th style={th}>Status</th>
                  <th style={th}>Employee</th>
                  <th style={th}>Period</th>
                  <th style={{ ...th, textAlign: 'right' }}>Entries</th>
                  <th style={{ ...th, textAlign: 'right' }}>Hours</th>
                  <th style={{ ...th, textAlign: 'right' }}>Standard</th>
                  <th style={{ ...th, textAlign: 'right' }}>OT (1.5x)</th>
                  <th style={{ ...th, textAlign: 'right' }}>Straight OT</th>
                  <th style={th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {preview.tabs.flatMap((tab) => {
                  const mappable = tab.status === 'matched' || tab.status === 'unmatched'
                  const currentMapping = mapping[tab.sheet_name] ?? ''
                  const employeeCell = mappable ? (
                    <select
                      value={currentMapping}
                      onChange={(e) => handleMappingChange(tab.sheet_name, e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">— skip this tab —</option>
                      {employees?.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.email})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ color: colors.gray50 }}>—</span>
                  )
                  const statusCell = (
                    <span style={{ color: statusColor(tab.status), fontWeight: 500 }}>
                      {statusLabel(tab.status)}
                    </span>
                  )

                  // Tabs without parsed periods (template_skipped, error) get one summary row.
                  if (tab.periods.length === 0) {
                    return [
                      <tr key={tab.sheet_name} style={{ borderBottom: `1px solid ${colors.gray10}` }}>
                        <td style={td}>{tab.sheet_name}</td>
                        <td style={td}>{statusCell}</td>
                        <td style={td}>{employeeCell}</td>
                        <td style={td} colSpan={6}>
                          <span style={{ color: colors.error, fontSize: 13 }}>{tab.error ?? '—'}</span>
                        </td>
                      </tr>,
                    ]
                  }

                  const span = tab.periods.length
                  return tab.periods.map((p, idx) => (
                    <tr
                      key={`${tab.sheet_name}::${p.start_date}`}
                      style={{ borderBottom: idx === span - 1 ? `1px solid ${colors.gray20}` : `1px solid ${colors.gray10}` }}
                    >
                      {idx === 0 && (
                        <>
                          <td style={td} rowSpan={span}>{tab.sheet_name}</td>
                          <td style={td} rowSpan={span}>{statusCell}</td>
                          <td style={td} rowSpan={span}>{employeeCell}</td>
                        </>
                      )}
                      <td style={td}>
                        {p.start_date} → {p.end_date}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>{p.entry_count}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                        {p.total_hours.toFixed(2)}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>{p.total_standard.toFixed(2)}</td>
                      <td style={{ ...td, textAlign: 'right', color: p.total_flsa_overtime > 0 ? colors.error : colors.gray50 }}>
                        {p.total_flsa_overtime > 0 ? p.total_flsa_overtime.toFixed(2) : '—'}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: p.total_straight_overtime > 0 ? colors.caution : colors.gray50 }}>
                        {p.total_straight_overtime > 0 ? p.total_straight_overtime.toFixed(2) : '—'}
                      </td>
                      {idx === 0 && (
                        <td style={{ ...td, color: colors.error, fontSize: 13 }} rowSpan={span}>
                          {tab.error ?? ''}
                        </td>
                      )}
                    </tr>
                  ))
                })}
              </tbody>
            </table>
          </div>
        )}

        {importResult && (
          <div style={successBox}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Imported {importResult.imported_tab_count} tab{importResult.imported_tab_count !== 1 ? 's' : ''} —{' '}
              {importResult.total_entry_count} entries, {importResult.total_hours.toFixed(2)} hours
            </div>
            {importResult.tabs.map((tab) => (
              <div key={tab.sheet_name} style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 500 }}>{tab.sheet_name}</div>
                {tab.timesheets.map((ts) => {
                  const action = ts.was_created ? 'created' : 'updated'
                  const parts: string[] = []
                  if (ts.entries_added > 0) parts.push(`${ts.entries_added} added`)
                  if (ts.entries_updated > 0) parts.push(`${ts.entries_updated} updated`)
                  return (
                    <div key={ts.timesheet_id} style={{ marginLeft: 12, fontSize: 13 }}>
                      Timesheet #{ts.timesheet_id} ({action}): {ts.start_date} → {ts.end_date} —{' '}
                      {parts.join(', ')}, {ts.total_hours.toFixed(2)} hours ({ts.status})
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        )}
      </Stack>
    </div>
  )
}

const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px' }
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' }
const selectStyle: React.CSSProperties = {
  padding: '4px 6px',
  borderRadius: 4,
  border: `1px solid ${colors.gray20}`,
  fontSize: 13,
  minWidth: 220,
}
const errorBox: React.CSSProperties = {
  padding: 12,
  background: colors.errorBackground,
  border: `1px solid ${colors.errorOutline}`,
  borderRadius: 6,
  color: colors.error,
  fontSize: 14,
}
const successBox: React.CSSProperties = {
  padding: 12,
  background: colors.successBackground,
  border: `1px solid ${colors.successOutline}`,
  borderRadius: 6,
  color: colors.success,
  fontSize: 14,
}

export const Route = createFileRoute('/org/import-bulk')({
  component: ImportBulkPage,
})
